import { apiClient } from './apiClient';
import type {
  AppraisalDetailResponse,
  UpdateInterviewCandidateRequest,
  UpdateSkillAreaFeedbackRequest,
  UpdateNotesToTalentCommitteeRequest,
  UpdatePerformanceSummaryRequest,
  SkillAreaDetail,
  EligibleProxy,
  ForwardLookingPlanItem,
  SelfAssessmentItem,
  InterviewSummaryEntry,
  AddInterviewSummaryEntryRequest,
  InterviewStaffSearchResult,
  AiExportResponse,
  AiImportRequest,
  PowerBiEmbedConfig,
} from './types/appraisal.types';
import type { AppraisalAuditRecord } from './types/audit.types';

export const appraisalDetailApi = {
  getAppraisalDetail: async (id: number): Promise<AppraisalDetailResponse> => {
    try {
      return await apiClient.get(`/appraisals/${id}`).then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch appraisal detail:', error);
      throw error;
    }
  },

  getUpwardFeedbackEmbedConfig: async (appraisalId: number): Promise<PowerBiEmbedConfig | null> => {
    try {
      const response = await apiClient.get(`/appraisals/${appraisalId}/upward-feedback/embed`);
      return response.status === 204 ? null : response.data;
    } catch (error) {
      console.error('Failed to fetch upward feedback embed config:', error);
      throw error;
    }
  },

  updateInterviewCandidate: async (
    appraisalId: number,
    candidateId: number,
    data: UpdateInterviewCandidateRequest,
    rowVersion?: string
  ): Promise<void> => {
    try {
      await apiClient.patch(`/appraisals/${appraisalId}/interview-candidates/${candidateId}`, { ...data, rowVersion });
    } catch (error) {
      console.error('Failed to update interview candidate:', error);
      throw error;
    }
  },

  updateSkillAreaFeedback: async (
    appraisalId: number,
    skillAreaId: number,
    data: UpdateSkillAreaFeedbackRequest,
    rowVersion?: string
  ): Promise<void> => {
    try {
      await apiClient.patch(`/appraisals/${appraisalId}/skill-area-feedback/${skillAreaId}`, { ...data, rowVersion });
    } catch (error) {
      console.error('Failed to update skill area feedback:', error);
      throw error;
    }
  },

  getSkillAreaDetails: async (
    appraisalId: number,
    skillAreaId: number
  ): Promise<SkillAreaDetail[]> => {
    try {
      return await apiClient
        .get(`/appraisals/${appraisalId}/skill-areas/${skillAreaId}/details`)
        .then((res) => res.data.skills);
    } catch (error) {
      console.error('Failed to fetch skill area details:', error);
      throw error;
    }
  },

  delegateAppraisal: async (appraisalId: number, delegateStaffId: number, rowVersion?: string): Promise<void> => {
    try {
      await apiClient.post(`/appraisals/${appraisalId}/delegate`, { delegateStaffId, rowVersion });
    } catch (error) {
      console.error('Failed to delegate appraisal:', error);
      throw error;
    }
  },

  revokeAppraisalDelegate: async (appraisalId: number, rowVersion?: string): Promise<void> => {
    try {
      await apiClient.delete(`/appraisals/${appraisalId}/delegate`, { data: { rowVersion } });
    } catch (error) {
      console.error('Failed to revoke appraisal delegate:', error);
      throw error;
    }
  },

  getEligibleProxies: async (appraisalId: number, search: string): Promise<EligibleProxy[]> => {
    try {
      return await apiClient
        .get(`/appraisals/${appraisalId}/eligible-proxies`, { params: { search } })
        .then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch eligible proxies:', error);
      throw error;
    }
  },

  updateForwardLookingPlan: async (
    appraisalId: number,
    items: ForwardLookingPlanItem[],
    rowVersion?: string
  ): Promise<void> => {
    try {
      await apiClient.put(`/appraisals/${appraisalId}/forward-looking-plan`, { items, rowVersion });
    } catch (error) {
      console.error('Failed to update forward looking plan:', error);
      throw error;
    }
  },

  updateNotesToTalentCommittee: async (
    appraisalId: number,
    data: UpdateNotesToTalentCommitteeRequest,
    rowVersion?: string
  ): Promise<void> => {
    try {
      await apiClient.patch(`/appraisals/${appraisalId}/notes-to-talent-committee`, { ...data, rowVersion });
    } catch (error) {
      console.error('Failed to update notes to talent committee:', error);
      throw error;
    }
  },

  updatePerformanceSummary: async (
    appraisalId: number,
    data: UpdatePerformanceSummaryRequest,
    rowVersion?: string
  ): Promise<void> => {
    try {
      await apiClient.patch(`/appraisals/${appraisalId}/performance-summary`, { ...data, rowVersion });
    } catch (error) {
      console.error('Failed to update performance summary:', error);
      throw error;
    }
  },

  getInterviewSummary: async (appraisalId: number): Promise<InterviewSummaryEntry[]> => {
    try {
      return await apiClient.get(`/appraisals/${appraisalId}/interview-summary`).then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch interview summary:', error);
      throw error;
    }
  },

  addInterviewSummaryEntry: async (
    appraisalId: number,
    data: AddInterviewSummaryEntryRequest
  ): Promise<InterviewSummaryEntry> => {
    try {
      return await apiClient
        .post(`/appraisals/${appraisalId}/interview-summary`, data)
        .then((res) => res.data);
    } catch (error) {
      console.error('Failed to add interview summary entry:', error);
      throw error;
    }
  },

  deleteInterviewSummaryEntry: async (appraisalId: number, candidateId: number): Promise<void> => {
    try {
      await apiClient.delete(`/appraisals/${appraisalId}/interview-summary/${candidateId}`);
    } catch (error) {
      console.error('Failed to delete interview summary entry:', error);
      throw error;
    }
  },

  searchInterviewStaff: async (
    appraisalId: number,
    query: string
  ): Promise<InterviewStaffSearchResult[]> => {
    try {
      return await apiClient
        .get(`/appraisals/${appraisalId}/interview-summary/staff-search`, { params: { q: query } })
        .then((res) => res.data);
    } catch (error) {
      console.error('Failed to search interview staff:', error);
      throw error;
    }
  },

  getInterviewSummaryReport: async (appraisalId: number): Promise<{ blob: Blob; fileName: string }> => {
    try {
      const res = await apiClient.get(`/appraisals/${appraisalId}/interview-summary/report`, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] ?? '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
      const fileName = match ? decodeURIComponent(match[1]) : `interview-summary-${appraisalId}.csv`;
      return { blob: res.data, fileName };
    } catch (error) {
      console.error('Failed to get interview summary report:', error);
      throw error;
    }
  },

  updateSelfAssessment: async (
    appraisalId: number,
    items: SelfAssessmentItem[],
    additionalContext?: string,
    rowVersion?: string
  ): Promise<void> => {
    try {
      await apiClient.put(`/appraisals/${appraisalId}/self-assessment`, { items, additionalContext, rowVersion });
    } catch (error) {
      console.error('Failed to update self assessment:', error);
      throw error;
    }
  },

  submitAppraisal: async (appraisalId: number, rowVersion?: string): Promise<void> => {
    try {
      await apiClient.post(`/appraisals/${appraisalId}/submit`, { rowVersion });
    } catch (error) {
      console.error('Failed to submit appraisal:', error);
      throw error;
    }
  },

  returnToAppraisee: async (appraisalId: number, rowVersion?: string): Promise<void> => {
    try {
      await apiClient.post(`/appraisals/${appraisalId}/return-to-appraisee`, { rowVersion });
    } catch (error) {
      console.error('Failed to return appraisal to appraisee:', error);
      throw error;
    }
  },

  getAuditHistory: async (appraisalId: number): Promise<AppraisalAuditRecord[]> => {
    try {
      return await apiClient.get(`/appraisals/${appraisalId}/audit-history`).then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch appraisal audit history:', error);
      throw error;
    }
  },

  exportForAi: async (appraisalId: number): Promise<AiExportResponse> => {
    try {
      return await apiClient.get(`/appraisals/${appraisalId}/ai-export`).then((res) => res.data);
    } catch (error) {
      console.error('Failed to export appraisal for AI:', error);
      throw error;
    }
  },

  importFromAi: async (appraisalId: number, data: AiImportRequest): Promise<AppraisalDetailResponse> => {
    try {
      return await apiClient.post(`/appraisals/${appraisalId}/ai-import`, data).then((res) => res.data);
    } catch (error) {
      console.error('Failed to import AI results:', error);
      throw error;
    }
  },

  confirmDelivery: async (appraisalId: number, rowVersion?: string): Promise<void> => {
    try {
      await apiClient.post(`/appraisals/${appraisalId}/confirm-delivery`, { rowVersion });
    } catch (error) {
      console.error('Failed to confirm delivery:', error);
      throw error;
    }
  },

  submitToTalentCommittee: async (appraisalId: number, rowVersion?: string): Promise<void> => {
    try {
      await apiClient.post(`/appraisals/${appraisalId}/submit-to-talent-committee`, { rowVersion });
    } catch (error) {
      console.error('Failed to submit to talent committee:', error);
      throw error;
    }
  },

  deliverToAppraisee: async (
    appraisalId: number,
    rowVersion?: string,
    deliveryDate?: string,
    deliveryTime?: string,
  ): Promise<void> => {
    try {
      await apiClient.post(`/appraisals/${appraisalId}/deliver-to-appraisee`, {
        rowVersion,
        deliveryDate,
        deliveryTime,
      });
    } catch (error) {
      console.error('Failed to deliver to appraisee:', error);
      throw error;
    }
  },

  getPriorAppraisal: async (appraisalId: number): Promise<{ appraisalId: number | null }> => {
    try {
      return await apiClient.get(`/appraisals/${appraisalId}/prior-appraisal`).then((res) => res.data);
    } catch (error) {
      console.error('Failed to get prior appraisal:', error);
      throw error;
    }
  },
};
